import type { MetadataCollector, MetadataCollectionResult } from './types.js';
import { withTimeout as withTimeoutUtil } from '../utils/index.js';

export abstract class BaseMetadataCollector<T = Record<string, unknown>> implements MetadataCollector<T> {
  protected name: string;

  constructor(name: string) {
    this.name = name;
  }

  getName(): string {
    return this.name;
  }

  async collect(): Promise<T> {
    const result = await this.collectWithResult();
    return result.data;
  }

  async collectWithResult(): Promise<MetadataCollectionResult<T>> {
    const startTime = Date.now();
    const collector = this.getName();

    try {
      const data = await this.collectMetadata();
      return {
        data,
        success: true,
        duration: Date.now() - startTime,
        collector,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.warn(`⚠️  TestDino: ${collector} metadata collection failed:`, errorMessage);
      return {
        data: this.getEmptyMetadata(),
        success: false,
        error: errorMessage,
        duration: Date.now() - startTime,
        collector,
      };
    }
  }

  protected abstract collectMetadata(): Promise<T>;
  protected abstract getEmptyMetadata(): T;

  protected async withTimeout<R>(promise: Promise<R>, timeoutMs: number, operation: string): Promise<R> {
    return withTimeoutUtil(promise, timeoutMs, operation);
  }

  protected safeJsonParse<R>(jsonString: string, fallback: R): R {
    try {
      return JSON.parse(jsonString);
    } catch {
      return fallback;
    }
  }

  protected isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }
}
